import { Suspense, useContext, useEffect, useState } from "react";
import {
  TableFilters,
  TableFiltersState,
} from "../../../../common/components/ui/table/TableFilters";
import Loading from "../../../../common/components/ui/loading/Loading";
import GenericTable from "../../../../common/components/ui/table/GenericTable";
import TableFooter from "../../../../common/components/ui/table/TableFooter";
import { useNavigate } from "react-router-dom";
import {
  IPurchaseOrder,
  IPurchaseOrderItem,
  PURCHASEORDER_STATUS,
} from "../../../../types/purchaseOrder.types";
import {
  approvePurchaseOrder,
  getPurchaseOrders,
  rejectPurchaseOrder,
  cancelPurchaseOrder,
  revalidar,
} from "../../../../common/services/purchaseOrder.api";
import ModalCreatePurchaseOrder from "../modals/ModalCreatePurchaseOrder";
import { useToastContext } from "../../../../common/components/ui/toast/toast.context";
import { useUser } from "../../../../common/services/users.api";
import ModalConfirmNfe from "../modals/ModalConfirmNfe";
import {
  getCompanies,
  getCompanyId,
} from "../../../../common/services/company.api";
import { AuthContext } from "../../../../common/components/auth/authContext/AuthContext";
import { formatDateAndTime } from "../../../../common/helpers/date.helpers";
import { FaInfoCircle } from "react-icons/fa";
import { numberToCurrency } from "../../../../common/helpers/currency.helpers";
import { Popover } from "flowbite-react";
import ModalConfirmStatus from "../modals/ModalConfirmStatus";
import { useCompanyStore } from "@/common/states/store/Company";
import { canAccessPurchaseOrderDashboard } from "../../purchaseOrderDashboardAccess";
import "./TablePurchaseOrder.scss";

const STATUS_LABELS = {
  [PURCHASEORDER_STATUS.APPROVED]: "Aprovada",
  [PURCHASEORDER_STATUS.PRE_APPROVED]: "Pre-Aprovada",
  [PURCHASEORDER_STATUS.PENDING]: "Pendente",
  [PURCHASEORDER_STATUS.REJECTED]: "Reprovada",
  [PURCHASEORDER_STATUS.CANCELED]: "Cancelada",
  [PURCHASEORDER_STATUS.PENDING_RELEASE]: "Lançamento Pendente",
};

type StatusCounterPurchaseOrder = {
  [key in PURCHASEORDER_STATUS]?: number | string;
} & {
  totalGeral?: number | string;
};

function getStatusLabels(status: PURCHASEORDER_STATUS) {
  return STATUS_LABELS[status] || status;
}

type PurchaseOrderTabStatus =
  | PURCHASEORDER_STATUS.CANCELED
  | PURCHASEORDER_STATUS.REJECTED
  | PURCHASEORDER_STATUS.PENDING
  | PURCHASEORDER_STATUS.PRE_APPROVED
  | PURCHASEORDER_STATUS.APPROVED;

const STATUS_TABS: { status: PurchaseOrderTabStatus; label: string }[] = [
  { status: PURCHASEORDER_STATUS.CANCELED, label: "Canceladas" },
  { status: PURCHASEORDER_STATUS.REJECTED, label: "Reprovadas" },
  { status: PURCHASEORDER_STATUS.PENDING, label: "Pendentes" },
  { status: PURCHASEORDER_STATUS.PRE_APPROVED, label: "Pre-aprovadas" },
  { status: PURCHASEORDER_STATUS.APPROVED, label: "Aprovadas" },
];

/** Status enviados à API quando há texto na busca (independente da aba). */
const PURCHASE_ORDER_SEARCH_ALL_STATUSES = [
  PURCHASEORDER_STATUS.CANCELED,
  PURCHASEORDER_STATUS.REJECTED,
  PURCHASEORDER_STATUS.PENDING,
  PURCHASEORDER_STATUS.PRE_APPROVED,
  PURCHASEORDER_STATUS.APPROVED,
  PURCHASEORDER_STATUS.PENDING_RELEASE,
];

/** Base RGB channels per tab (same values as Tailwind-style `rgb(r g b / …)`) */
const TAB_STATUS_RGB: Record<PurchaseOrderTabStatus, string> = {
  [PURCHASEORDER_STATUS.CANCELED]: "107 114 128",
  [PURCHASEORDER_STATUS.REJECTED]: "185 28 28",
  [PURCHASEORDER_STATUS.PENDING]: "64 72 209",
  [PURCHASEORDER_STATUS.PRE_APPROVED]: "242 187 22",
  [PURCHASEORDER_STATUS.APPROVED]: "51 102 24",
};

function tabSurface(status: PurchaseOrderTabStatus, active: boolean): string {
  const rgb = TAB_STATUS_RGB[status];
  if (active) {
    return `rgb(${rgb} / var(--tw-text-opacity, 1))`;
  }
  return `rgb(${rgb} / 0.22)`;
}

function tabLabelColor(status: PurchaseOrderTabStatus, active: boolean): string {
  if (!active) return "#4b5563";
  if (status === PURCHASEORDER_STATUS.PRE_APPROVED) return "#111827";
  return "#ffffff";
}


/** Contadores da API podem vir como string com milhar pt-BR (ex.: "12.450"); Number("12.450") vira 12.45. */
function parseStatusCountValue(value: number | string | undefined): number {
  if (value == null || value === "") return 0;
  if (typeof value === "number") {
    return Number.isFinite(value) ? Math.trunc(value) : 0;
  }
  const s = String(value).trim();
  if (/^\d+$/.test(s)) return parseInt(s, 10);
  /* 12.450 ou 1.234.567 — ponto como separador de milhar */
  if (/^\d{1,3}(\.\d{3})+$/.test(s)) {
    return parseInt(s.replace(/\./g, ""), 10);
  }
  /* 12,450 — vírgula como milhar (menos comum na API) */
  if (/^\d{1,3}(,\d{3})+$/.test(s)) {
    return parseInt(s.replace(/,/g, ""), 10);
  }
  const n = Number(s);
  return Number.isFinite(n) ? Math.trunc(n) : 0;
}

function formatStatusCount(
  value: number | string | undefined
): string {
  const n = parseStatusCountValue(value);
  return n.toLocaleString("pt-BR");
}

function TablePurchaseOrder() {
  useContext(AuthContext);
  const companyId = getCompanyId();
  const companyStore = useCompanyStore();
  const [page, setPage] = useState<number>(1);
  const [data, setData] = useState({ itemsPerPage: 10, totalItems: 0 });
  const [purchaseOrders, setPurchaseOrders] = useState<IPurchaseOrder[]>([]);
  const [statusCounter, setStatusCounter] =
    useState<StatusCounterPurchaseOrder>({});
  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [PURCHASEORDER_STATUS.PENDING],
    orderBy: "-createdAt",
  });
  const EMPTY_ACTION = { eventName: "", data: undefined };
  const [action, setAction] = useState<{ eventName: string; data?: unknown }>(
    EMPTY_ACTION
  );
  const { notifySuccess, notifyError } = useToastContext();
  const [companies, setCompanies] = useState<CompanyAccess>([]);
  const user = useUser();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const isAction = (eventName: string) => action.eventName === eventName;

  const cleanActionData = () => {
    setAction(EMPTY_ACTION);
  };

  type CompanyAccess = {
    companyId: string;
    companyName: string;
  }[];

  useEffect(() => {
    async function loadPurchaseOrders() {
      setIsLoading(true);
      try {
        const searchTrimmed = (filters.search ?? "").trim();
        const statusForRequest =
          searchTrimmed.length > 0
            ? PURCHASE_ORDER_SEARCH_ALL_STATUSES
            : filters.status;

        const result = await getPurchaseOrders({
          page,
          orderBy: filters.orderBy || "-createdAt",
          status: statusForRequest,
          search: filters.search,
          itemsPerPage: filters.itemsPerPage ?? "10",
          supplierId: filters.supplierId,
          requestorName:
            filters.filterMyPurchaseOrders && user.name
              ? user.name
              : filters.requestorName,
          categoryId: filters.categoryId,
          costCenterId: filters.costCenterId,
        });
        const resultCompanies = await getCompanies();
        setCompanies(resultCompanies.companies);
        setData({
          itemsPerPage: Number(filters.itemsPerPage ?? 10),
          totalItems: result.totalItems,
        });
        if (result.statusCounter) {
          setStatusCounter(
            result.statusCounter as string as unknown as StatusCounterPurchaseOrder
          );
        }
        setPurchaseOrders(result.data);
      } finally {
        setIsLoading(false);
      }
    }
    loadPurchaseOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, data.itemsPerPage, filters, page, companyStore]);

  const handleApprovedPurchaseOrder = async (_id: string) => {
    const result = await approvePurchaseOrder({
      _id: _id,
      requestorEmail: user.email as string,
      requestorName: user.name as string,
    });
    cleanActionData();
    if (!result.ok) {
      notifyError(result.message as string);
      return;
    }

    if (result.data.status === PURCHASEORDER_STATUS.APPROVED) {
      notifySuccess("Ordem de Compra Pré-Aprovada com sucesso");
      setFilters({ ...filters });
      return;
    }

    notifySuccess("Ordem de Compra Aprovada com sucesso");
  };

  const handleReprovedPurchaseOrder = async (data: IPurchaseOrder) => {
    const result = await rejectPurchaseOrder(data._id as string);
    if (result.ok) {
      notifySuccess("Reprovado com Sucesso a Ordem de Compra");
      setFilters({ ...filters });
      cleanActionData();
      return;
    }
    if (!result.ok) {
      notifyError(result.message as string);
    }
  };

  const handleRemovePurchaseOrder = async (data: IPurchaseOrder) => {
    const result = await cancelPurchaseOrder(data._id as string);
    if (result.ok) {
      notifySuccess("Removido com Sucesso a Ordem de Compra");
      setFilters({ ...filters });
      cleanActionData();
      return;
    }
    if (!result.ok) {
      notifyError(result.message as string);
    }
  };

  /*const actionPostInventory = async (data: IPurchaseOrderItem) => {
    setAction({ eventName: 'postInventory', data: data })
  }*/

  const actions = [
    {
      label: "Visualizar",
      permission: "edit:inventory-pieces",
      action: (data: unknown) => {
        const order = data as IPurchaseOrder;
        navigate(`/compras/ordem/${order._id}`);
      },
    },
    {
      label: "Aprovar",
      action: (data) => {
        setAction({ eventName: "approve", data });
      },
      permission: "edit:inventory-pieces",
      disabled: (data) => {
        return (
          (data.status == "approved" || getCompanyId() == "*" ? true : false) ||
          data?.requestorName == user.name
        );
      },
      visible: (data) => {
        return (
          (data.status == "pending" || data.status == "pre-approved") &&
          getCompanyId() != "*"
        );
      },
    },
    {
      label: "Reprovar",
      action: (data) => {
        setAction({ eventName: "reprove", data });
      },
      permission: "edit:inventory-pieces",
      visible: () => {
        return getCompanyId() != "*";
      },
    },
    {
      label: "Remover",
      action: (data) => {
        setAction({ eventName: "remove", data });
      },
      permission: "edit:inventory-pieces",
      visible: () => {
        return getCompanyId() != "*";
      },
    },
    {
      label: "Revalidar",
      action: (data) => {
        (async function () {
          const result = await revalidar(data._id);
          if (!result.ok) {
            notifyError(result.message as string);
          } else {
            notifySuccess("Revalidado com sucesso");
          }
          setAction({ eventName: "revalidate", data });
        })();
      },
      permission: "edit:inventory-pieces",
      visible: (data) => {
        return getCompanyId() != "*" && data.status == "canceled";
      },
    },
    /*{
      label: "Lançar Estoque",
      permission: "edit:inventory-pieces",
      action: (data) => actionPostInventory(data),
      visible: (data) => {
        return (PURCHASEORDER_STATUS.PENDING_RELEASE && !data.inventoryLaunched && getCompanyId() != "*")
      }
    },*/
  ];

  const formatStyle = (
    content: React.ReactNode,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: Record<string, any>
  ): React.ReactNode => {
    if (data.status == "approved") {
      return <span className="text-greenDark font-semibold">{content}</span>;
    } else if (data.status == "pending") {
      return <span className="text-bluePrimary font-semibold">{content}</span>;
    } else if (data.status == "rejected") {
      return <span className="text-redPrimary font-semibold">{content}</span>;
    } else if (data.status == "pending-release") {
      return <span className="text-black font-semibold">{content}</span>;
    } else if (data.status == "pre-approved") {
      return (
        <span className="text-yellowPrimary font-semibold">{content}</span>
      );
    } else if (data.status == "canceled") {
      return <span className="text-gray">{content}</span>;
    }
  };
  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    setPage(1);
    setFilters((prevFilters) => {
      const merged = { ...prevFilters, ...newFilters };
      /* TableFilters envia status: [] no mount e nas buscas; com abas o status vem só das abas */
      if (
        Array.isArray(newFilters.status) &&
        newFilters.status.length === 0
      ) {
        merged.status = prevFilters.status;
      }
      return merged;
    });
  };

  const activeStatusTab =
    (filters.status?.find((s) =>
      STATUS_TABS.some((t) => t.status === s)
    ) as PurchaseOrderTabStatus | undefined) ?? PURCHASEORDER_STATUS.PENDING;

  const handleStatusTabChange = (status: PurchaseOrderTabStatus) => {
    setPage(1);
    setFilters((prev) => ({
      ...prev,
      status: [status],
    }));
  };
  return (
    <Suspense fallback={<Loading />}>
      <div className="table-section">
        <TableFilters
          dashboardHeader="Ordem de Compras"
          tableData={purchaseOrders}
          placeholder="Digite código, fornecedor, descrição, categoria, centro de custo ou autor"
          statusOptions={STATUS_LABELS}
          hideStatusFilter
          customOrderBy="-createdAt"
          newItemLabel="Nova Ordem de Compra"
          onNewItemClick={() =>
            setAction({ eventName: "create", data: undefined })
          }
          onSearch={handleFilterChange}
          entity="inventory-pieces"
          userName={user.name ?? ""}
        />
        {canAccessPurchaseOrderDashboard(user.email) && (
          <div className="flex justify-start -mt-4 mb-6">
            <a
              href="/compras/ordem/dashboard"
              onClick={(e) => {
                e.preventDefault();
                navigate("/compras/ordem/dashboard");
              }}
              className="text-sm font-medium hover:underline"
              style={{ color: "rgb(228,65,49)" }}
            >
              Dashboard
            </a>
          </div>
        )}
        <div className="purchase-order-status-tabs-and-table">
        <ul
          className="flex -mb-px mt-2"
          role="tablist"
          aria-label="Filtrar por status"
          style={{ borderBottom: "1px solid #e5e7eb" }}
        >
          {STATUS_TABS.map(({ status, label }) => {
            const isActive = activeStatusTab === status;
            const rgb = TAB_STATUS_RGB[status];
            const count = statusCounter[status];
            const badgeActive =
              status === PURCHASEORDER_STATUS.PRE_APPROVED
                ? {
                    backgroundColor: "rgba(0,0,0,0.14)",
                    color: "#111827",
                  }
                : {
                    backgroundColor: "rgba(255,255,255,0.28)",
                    color: "#ffffff",
                  };
            return (
              <li key={status} className="-mb-px" role="presentation">
                <button
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => handleStatusTabChange(status)}
                  className="cursor-pointer outline-none focus:outline-none focus:ring-0 focus:shadow-none focus-visible:outline-none focus-visible:ring-0"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "10px 20px",
                    fontSize: "14px",
                    fontWeight: 500,
                    borderRadius: "6px 6px 0 0",
                    border: "1px solid transparent",
                    marginBottom: "-1px",
                    transition:
                      "color 0.15s, border-color 0.15s, background-color 0.15s",
                    color: tabLabelColor(status, isActive),
                    backgroundColor: tabSurface(status, isActive),
                    ...(isActive
                      ? {
                          borderColor: `rgb(${rgb} / var(--tw-text-opacity, 1)) rgb(${rgb} / var(--tw-text-opacity, 1)) #fff`,
                        }
                      : {
                          borderColor: "transparent",
                        }),
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.backgroundColor = `rgb(${rgb} / 0.34)`;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.backgroundColor = tabSurface(
                        status,
                        false
                      );
                    }
                  }}
                >
                  {label}
                  <span
                    className="tabular-nums"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: "24px",
                      padding: "1px 8px",
                      fontSize: "12px",
                      fontWeight: 600,
                      borderRadius: "10px",
                      ...(isActive
                        ? badgeActive
                        : {
                            backgroundColor: "rgba(255,255,255,0.75)",
                            color: "#374151",
                          }),
                    }}
                  >
                    {formatStatusCount(count)}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
        {isLoading ? (
          <div
            className="purchase-order-table-loading-shell"
            aria-busy="true"
            aria-live="polite"
          >
            <div className="purchase-order-table-loading-shell__body">
              <div className="flex flex-col items-center gap-3">
                <div
                  className="purchase-order-table-loading-spinner"
                  role="status"
                  aria-label="Carregando"
                />
                <span className="text-sm text-gray-600">Carregando…</span>
              </div>
            </div>
          </div>
        ) : (
          <GenericTable
          headers={{
            createdAt: "Data",
            companyId: "Filial",
            code: "Código",
            requestorName: "Solicitante",
            supplierName: "Fornecedor",
            status: "Status",
            financial: "Valor",
          }}
          entity="purchase-order"
          actions={actions}
          data={purchaseOrders}
          setFilters={({ orderBy }) => handleFilterChange({ orderBy })}
          renders={{
            createdAt: (createdAt, purchaseOrder) => {
              const formattedDate = formatDateAndTime(createdAt);
              return formatStyle(
                <span
                  className="flex items-center gap-2"
                  style={{ fontSize: "15px" }}
                >
                  {formattedDate || ""}
                </span>,
                purchaseOrder as IPurchaseOrder
              ) as React.ReactElement;
            },
            code: (code, purchaseOrder) => {
              return formatStyle(
                <a
                  href={`compras/ordem/${(purchaseOrder as IPurchaseOrder)._id
                    }`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(
                      `/compras/ordem/${(purchaseOrder as IPurchaseOrder)._id}`
                    );
                  }}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span style={{ fontWeight: 600 }}>{code as string}</span>
                </a>,
                purchaseOrder as IPurchaseOrder
              ) as React.ReactElement;
            },
            requestorName: (requestorName, purchaseOrder) => {
              return formatStyle(
                requestorName as string,
                purchaseOrder as IPurchaseOrder
              ) as React.ReactElement;
            },
            supplierName: (supplierName, purchaseOrder) => {
              return formatStyle(
                supplierName as string,
                purchaseOrder as IPurchaseOrder
              ) as React.ReactElement;
            },
            financial: (data: unknown, context: unknown) => {
              const financial = data as { value: string };
              const purchaseOrder = context as IPurchaseOrder;

              return formatStyle(
                <div className="flex items-center gap-2">
                  {purchaseOrder.description && (
                    <Popover
                      content={
                        <div className="bg-white text-black border border-gray-300 rounded-md shadow-md p-2 max-w-sm z-50">
                          <span className="text-sm">
                            {purchaseOrder.description}
                          </span>
                        </div>
                      }
                      placement="top"
                      trigger="click"
                    >
                      <button className="cursor-pointer text-blue-600">
                        <FaInfoCircle />
                      </button>
                    </Popover>
                  )}
                  <span>{numberToCurrency(financial?.value)}</span>
                </div>,
                purchaseOrder
              ) as React.ReactElement;
            },
            status: (status, purchaseOrder) => {
              return formatStyle(
                getStatusLabels(status as PURCHASEORDER_STATUS),
                purchaseOrder as IPurchaseOrder
              ) as React.ReactElement;
            },
            votesApproved: (votesApproved, purchaseOrder) => {
              if ((votesApproved as { requestorName: string }[])?.length) {
                return formatStyle(
                  (votesApproved as { requestorName: string }[])[0]
                    .requestorName,
                  purchaseOrder as IPurchaseOrder
                ) as React.ReactElement;
              } else {
                return formatStyle(
                  "Pendente",
                  purchaseOrder as IPurchaseOrder
                ) as React.ReactElement;
              }
            },
            companyId: (companyId, purchaseOrder) => {
              if (!companies) {
                return "";
              }
              return formatStyle(
                companies.find((company) => company.companyId === companyId)
                  ?.companyName as string,
                purchaseOrder as IPurchaseOrder
              ) as React.ReactElement;
            },
          }}
        />
        )}
        </div>
        {isAction("postInventory") && (
          <ModalConfirmNfe
            purchaseOrderId={(action.data as IPurchaseOrderItem)._id as string}
            onClose={cleanActionData}
          ></ModalConfirmNfe>
        )}
        {isAction("approve") && (
          <ModalConfirmStatus
            open={true}
            title="Confirmar Aprovação"
            description="Tem certeza que deseja aprovar esta ordem de compra?"
            confirmLabel="Sim, aprovar"
            cancelLabel="Cancelar"
            confirmType="approve"
            onCancel={cleanActionData}
            onConfirm={() =>
              handleApprovedPurchaseOrder(
                (action.data as IPurchaseOrder)._id as string
              )
            }
          />
        )}

        {isAction("reprove") && (
          <ModalConfirmStatus
            open={true}
            title="Confirmar Reprovação"
            description="Tem certeza que deseja reprovar esta ordem de compra?"
            confirmLabel="Sim, reprovar"
            cancelLabel="Cancelar"
            confirmType="reject"
            onCancel={cleanActionData}
            onConfirm={() =>
              handleReprovedPurchaseOrder(action.data as IPurchaseOrder)
            }
          />
        )}

        {isAction("remove") && (
          <ModalConfirmStatus
            open={true}
            title="Confirmar Remoção"
            description="Tem certeza que deseja remover esta ordem de compra?"
            confirmLabel="Sim, remover"
            cancelLabel="Cancelar"
            confirmType="remove"
            onCancel={cleanActionData}
            onConfirm={() =>
              handleRemovePurchaseOrder(action.data as IPurchaseOrder)
            }
          />
        )}

        {!isLoading && (
          <TableFooter
            page={page || 1}
            totalItems={data.totalItems || 0}
            itemsPerPage={data.itemsPerPage || 10}
            goToPage={setPage}
          />
        )}
      </div>
      {isAction("create") && (
        <ModalCreatePurchaseOrder onClose={cleanActionData} />
      )}
    </Suspense>
  );
}

export default TablePurchaseOrder;
